import {
  CefrLevel,
  ScoreComponent,
  ScoreComponentKind,
  StageScoringComponentConfigInput,
} from 'generated/graphql';

/** Mirrors `WEIGHT_SUM_TOLERANCE` in the monolith's `scoring-config.ts`. */
export const WEIGHT_SUM_TOLERANCE = 0.001;

export const SCORE_COMPONENT_LABELS: Record<ScoreComponent, string> = {
  [ScoreComponent.Psychometric]: 'Psychometric overall',
  [ScoreComponent.CognitiveSummary]: 'Cognitive summary',
  [ScoreComponent.Coderbyte]: 'Coderbyte overall',
  [ScoreComponent.EnglishProficiency]: 'English proficiency',
};

export const SCORE_KIND_LABELS: Record<ScoreComponentKind, string> = {
  [ScoreComponentKind.Compensatory]: 'Compensatory',
  [ScoreComponentKind.Knockout]: 'Knockout',
};

export const formatCefrLevel = (level: CefrLevel): string =>
  level === CefrLevel.BelowA1 ? 'Below A1' : level;

/** Form state of one row; numbers stay strings so half-typed input survives. */
export type ScoringRowDraft = {
  component: ScoreComponent;
  kind: ScoreComponentKind;
  weight: string;
  threshold: string;
};

/** Only non-English knockouts take a threshold (English uses the CEFR level). */
export const takesThreshold = (
  row: Pick<ScoringRowDraft, 'component' | 'kind'>
) =>
  row.kind === ScoreComponentKind.Knockout &&
  row.component !== ScoreComponent.EnglishProficiency;

const parseNumber = (value: string): number =>
  value.trim() === '' ? NaN : Number(value);

export const totalWeight = (rows: readonly ScoringRowDraft[]): number =>
  rows.reduce((sum, row) => {
    const weight = parseNumber(row.weight);
    return Number.isFinite(weight) ? sum + weight : sum;
  }, 0);

export const isWeightTotalValid = (total: number): boolean =>
  Math.abs(total - 1) <= WEIGHT_SUM_TOLERANCE;

/** Float noise like 0.30000000000000004 must not reach the screen. */
export const formatWeightTotal = (total: number): string =>
  String(Math.round(total * 10000) / 10000);

/** The client-side twin of the server's `validateScoringConfig`. */
export const validateScoringDrafts = (
  rows: readonly ScoringRowDraft[]
): string[] => {
  const errors: string[] = [];
  let allWeightsValid = true;
  for (const row of rows) {
    const label = SCORE_COMPONENT_LABELS[row.component];
    const weight = parseNumber(row.weight);
    if (!Number.isFinite(weight) || weight < 0 || weight > 1) {
      errors.push(`${label}: weight must be a number between 0 and 1.`);
      allWeightsValid = false;
    }
    if (takesThreshold(row) && row.threshold.trim() !== '') {
      const threshold = parseNumber(row.threshold);
      if (!Number.isInteger(threshold) || threshold < 0 || threshold > 100) {
        errors.push(`${label}: threshold must be an integer from 0 to 100.`);
      }
    }
  }
  const total = totalWeight(rows);
  if (allWeightsValid && !isWeightTotalValid(total)) {
    errors.push(
      `Weights must add up to 1 (they add up to ${formatWeightTotal(total)}).`
    );
  }
  return errors;
};

export const toMutationComponents = (
  rows: readonly ScoringRowDraft[]
): StageScoringComponentConfigInput[] =>
  rows.map(row => {
    const input: StageScoringComponentConfigInput = {
      component: row.component,
      kind: row.kind,
      weight: Number(row.weight),
    };
    if (takesThreshold(row) && row.threshold.trim() !== '') {
      input.threshold = Number(row.threshold);
    }
    return input;
  });
